/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { useMutation, useQuery } from '@apollo/client'
import { Close, Download, UploadFile } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import { useState } from 'react'

import {
  BULK_CREATE_USERS,
  BULK_SEND_INVITATIONS,
  BulkCreateUsersMutation,
  BulkCreateUsersMutationVariables,
  BulkSendInvitationsMutation,
  BulkSendInvitationsMutationVariables,
  DAIPermissionCodename,
  GET_TENANT_STORES_SIMPLE,
  GetTenantStoresSimpleQuery,
  GetTenantStoresSimpleQueryVariables,
  GroupFragment,
  Ordering,
  PARSE_CREATE_USER_CSV,
  ParseCreateUserCsvMutation,
  ParseCreateUserCsvMutationVariables,
} from '@/graphql'
import useDaiGroups from '@/hooks/use-dai-groups'
import useT from '@/i18n/useT'

import ImportUsersCsvTable from './ImportUsersCsvTable'

const CSV_SCHEMA_COLUMNS = [
  'Name',
  'Email',
  'Role',
  'Pin',
  'Assigned Store Name',
  'Address1',
  'City',
  'State',
]
const PIN_PATTERN = /^\d{4,10}$/
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export type EditableUserRow = {
  key: string
  name: string
  nameError: string | null
  roleId: string
  roleError: string | null
  storeId: string
  storeError: string | null
  pin: string
  pinError: string | null
  email: string
  emailError: string | null
  created: boolean
  submitError: string | null
}

export const groupRequiresEmail = (group: GroupFragment) =>
  group.permissions.some(
    permission =>
      permission.codename === DAIPermissionCodename.CAN_LOGIN_TO_WEB_APP,
  )

const rowFromParsedData = (
  data: ParseCreateUserCsvMutation['parseCreateUserCsv'][number],
  index: number,
): EditableUserRow => ({
  key: `${index}`,
  name: data.name.value,
  nameError: data.name.error,
  roleId: data.role.id ?? '',
  roleError: data.role.error,
  storeId: data.assignedStoreName.id ?? '',
  storeError: data.assignedStoreName.error,
  pin: data.pin.value ?? '',
  pinError: data.pin.error,
  email: data.email.value ?? '',
  emailError: data.email.error,
  created: false,
  submitError: null,
})

const readFirstLine = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () =>
      resolve(((reader.result as string) ?? '').split(/\r?\n/)[0] ?? '')
    reader.onerror = () => reject(reader.error)
    reader.readAsText(file)
  })

const downloadCsvTemplate = () => {
  const blob = new Blob([`${CSV_SCHEMA_COLUMNS.join(',')}\n`], {
    type: 'text/csv',
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'import-users-template.csv'
  link.click()
  URL.revokeObjectURL(url)
}

interface Props {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

const ImportUsersCsvDialog: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useT()

  const [fileError, setFileError] = useState<string | null>(null)
  const [parsing, setParsing] = useState(false)
  const [rows, setRows] = useState<EditableUserRow[] | null>(null)
  const [creating, setCreating] = useState(false)
  const [createProgress, setCreateProgress] = useState(0)

  const {
    groups,
    adminGroup,
    loading: groupsLoading,
    error: groupsError,
  } = useDaiGroups()

  const { data: storesData, loading: storesLoading } = useQuery<
    GetTenantStoresSimpleQuery,
    GetTenantStoresSimpleQueryVariables
  >(GET_TENANT_STORES_SIMPLE, {
    variables: {
      pagination: { limit: 500, offset: 0 },
      order: { name: Ordering.ASC },
    },
    skip: !open,
  })
  const stores = (storesData?.tenantStores.results ?? []).filter(
    (
      store,
    ): store is typeof store & {
      location: NonNullable<typeof store.location>
    } => store.location !== null,
  )

  const [parseCreateUserCsv] = useMutation<
    ParseCreateUserCsvMutation,
    ParseCreateUserCsvMutationVariables
  >(PARSE_CREATE_USER_CSV)

  const [bulkSendInvitations] = useMutation<
    BulkSendInvitationsMutation,
    BulkSendInvitationsMutationVariables
  >(BULK_SEND_INVITATIONS)

  const [bulkCreateUsers] = useMutation<
    BulkCreateUsersMutation,
    BulkCreateUsersMutationVariables
  >(BULK_CREATE_USERS)

  const resetState = () => {
    setFileError(null)
    setParsing(false)
    setRows(null)
    setCreating(false)
    setCreateProgress(0)
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    setFileError(null)
    setRows(null)

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setFileError(
        t('importUsersCsv.error.notCsv', 'Please upload a .csv file.'),
      )
      return
    }

    setParsing(true)
    try {
      const headers = (await readFirstLine(file))
        .split(',')
        .map(header => header.trim().toLowerCase())
      const expectedHeaders = CSV_SCHEMA_COLUMNS.map(column =>
        column.toLowerCase(),
      )
      const headersMatch =
        headers.length === expectedHeaders.length &&
        expectedHeaders.every((column, index) => headers[index] === column)

      if (!headersMatch) {
        setFileError(
          t(
            'importUsersCsv.error.invalidHeaders',
            `The CSV headers must exactly match: ${CSV_SCHEMA_COLUMNS.join(', ')}`,
          ),
        )
        return
      }

      const result = await parseCreateUserCsv({ variables: { file } })
      const parsed = result.data?.parseCreateUserCsv ?? []
      setRows(parsed.map(rowFromParsedData))
    } catch (err) {
      setFileError(
        err instanceof Error
          ? err.message
          : t(
              'importUsersCsv.error.failedToParse',
              'Failed to parse the CSV file.',
            ),
      )
    } finally {
      setParsing(false)
    }
  }

  const updateRow = (key: string, changes: Partial<EditableUserRow>) => {
    setRows(prev =>
      prev
        ? prev.map(row => (row.key === key ? { ...row, ...changes } : row))
        : prev,
    )
  }

  const rowValidation = (row: EditableUserRow) => {
    const isAdmin = row.roleId !== '' && row.roleId === adminGroup?.id
    const nameError =
      row.nameError ||
      (row.name.trim()
        ? null
        : t('importUsersCsv.error.nameRequired', 'Name is required'))
    const selectedGroup = groups.find(group => group.id === row.roleId)
    const roleRequiresEmail = Boolean(
      selectedGroup && groupRequiresEmail(selectedGroup),
    )
    const roleError =
      row.roleError ||
      (row.roleId
        ? roleRequiresEmail && !row.email.trim()
          ? t(
              'importUsersCsv.error.roleRequiresEmail',
              'This role requires an email address',
            )
          : null
        : t('importUsersCsv.error.roleRequired', 'Role is required'))
    const storeError = isAdmin
      ? null
      : row.storeError ||
        (row.storeId
          ? null
          : t(
              'importUsersCsv.error.storeRequired',
              'Assigned store is required',
            ))
    const pinError =
      row.pinError ||
      (row.pin && !PIN_PATTERN.test(row.pin)
        ? t('importUsersCsv.error.invalidPin', 'Pin must be 4-10 digits')
        : null)
    const emailError =
      row.emailError ||
      (row.email && !EMAIL_PATTERN.test(row.email)
        ? t('importUsersCsv.error.invalidEmail', 'Email is not valid')
        : null)
    return {
      nameError,
      roleError,
      storeError,
      pinError,
      emailError,
      isAdmin,
    }
  }

  const hasBlockingErrors =
    !rows ||
    rows.length === 0 ||
    rows.some(row => {
      const { nameError, roleError, storeError, pinError, emailError } =
        rowValidation(row)
      return Boolean(
        nameError || roleError || storeError || pinError || emailError,
      )
    })

  const handleCreateUsers = async () => {
    if (!rows || rows.length === 0 || hasBlockingErrors) return

    setCreating(true)
    setCreateProgress(0)

    const pendingRows = rows.filter(row => !row.created)
    const rowsWithEmail = pendingRows.filter(row => row.email.trim())
    const rowsWithoutEmail = pendingRows.filter(row => !row.email.trim())

    let allSucceeded = true

    if (rowsWithEmail.length > 0) {
      try {
        await bulkSendInvitations({
          variables: {
            data: rowsWithEmail.map(row => {
              const { isAdmin } = rowValidation(row)
              return {
                email: row.email.trim(),
                groupIds: [row.roleId],
                storeIds: isAdmin ? [] : [row.storeId],
              }
            }),
          },
        })
        rowsWithEmail.forEach(row =>
          updateRow(row.key, { created: true, submitError: null }),
        )
      } catch (err) {
        allSucceeded = false
        const message =
          err instanceof Error
            ? err.message
            : t(
                'importUsersCsv.error.failedToInvite',
                'Failed to send invitation',
              )
        rowsWithEmail.forEach(row =>
          updateRow(row.key, { created: false, submitError: message }),
        )
      }
      setCreateProgress(prev => prev + rowsWithEmail.length)
    }

    if (rowsWithoutEmail.length > 0) {
      try {
        await bulkCreateUsers({
          variables: {
            data: rowsWithoutEmail.map(row => {
              const { isAdmin } = rowValidation(row)
              return {
                name: row.name.trim(),
                roleId: row.roleId,
                storeId: isAdmin ? null : row.storeId,
                pin: row.pin || null,
              }
            }),
          },
        })
        rowsWithoutEmail.forEach(row =>
          updateRow(row.key, { created: true, submitError: null }),
        )
      } catch (err) {
        allSucceeded = false
        const message =
          err instanceof Error
            ? err.message
            : t('importUsersCsv.error.failedToCreate', 'Failed to create user')
        rowsWithoutEmail.forEach(row =>
          updateRow(row.key, { created: false, submitError: message }),
        )
      }
      setCreateProgress(prev => prev + rowsWithoutEmail.length)
    }

    setCreating(false)
    if (allSucceeded) {
      resetState()
      onSuccess?.()
    }
  }

  const allRowsCreated =
    rows !== null && rows.length > 0 && rows.every(row => row.created)

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6">
            {t('importUsersCsv.title', 'Import Users from CSV')}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {fileError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setFileError(null)}
          >
            {fileError}
          </Alert>
        )}
        {rows && rows.length === 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t(
              'importUsersCsv.error.noRows',
              'The CSV file contains no data rows.',
            )}
          </Alert>
        )}
        {allRowsCreated && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t(
              'importUsersCsv.status.allCreated',
              'All users were created successfully.',
            )}
          </Alert>
        )}

        {!rows && (
          <Box sx={{ py: 4 }}>
            <Stack alignItems="center" spacing={2}>
              <UploadFile sx={{ fontSize: 64, color: 'primary.main' }} />
              <Typography variant="body1">
                {t(
                  'importUsersCsv.description',
                  'Upload a CSV file with the following column headers, in this order:',
                )}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                fontFamily="monospace"
              >
                {CSV_SCHEMA_COLUMNS.join(', ')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t(
                  'importUsersCsv.optionalFieldsDescription',
                  'Pin and Email are optional; leave them blank to fill in later. Some roles require an email to be set.',
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t(
                  'importUsersCsv.storeDescription',
                  'Store name is required. Address fields are optional unless multiple stores share the same name.',
                )}
              </Typography>
              {parsing ? (
                <CircularProgress />
              ) : (
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={downloadCsvTemplate}
                  >
                    {t('importUsersCsv.downloadTemplate', 'Download Template')}
                  </Button>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<UploadFile />}
                  >
                    {t('importUsersCsv.chooseFile', 'Choose File')}
                    <input
                      type="file"
                      hidden
                      accept=".csv"
                      onChange={handleFileChange}
                    />
                  </Button>
                </Stack>
              )}
            </Stack>
          </Box>
        )}

        {rows && (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t(
                'importUsersCsv.reviewDescription',
                'Review and correct the parsed rows below before creating users.',
              )}
            </Typography>
            {creating && (
              <Box sx={{ mb: 2 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('importUsersCsv.creating', 'Creating users...')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {createProgress} / {rows.length}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={
                    rows.length > 0 ? (createProgress / rows.length) * 100 : 0
                  }
                />
              </Box>
            )}
            <ImportUsersCsvTable
              rows={rows}
              rowValidation={rowValidation}
              updateRow={updateRow}
              groups={groups}
              groupsLoading={groupsLoading}
              groupsError={groupsError}
              adminGroup={adminGroup}
              stores={stores}
              storesLoading={storesLoading}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={creating}>
          {t('common.cancel')}
        </Button>
        {rows && (
          <Button
            variant="contained"
            onClick={handleCreateUsers}
            disabled={creating || hasBlockingErrors || allRowsCreated}
          >
            {creating
              ? t('importUsersCsv.creating', 'Creating users...')
              : t('importUsersCsv.submit', 'Create Users')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ImportUsersCsvDialog
